import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentOrgId } from '../auth';
import { queryConfigs } from '@/lib/queryClient';

export interface OrganizationUser {
  user_id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  role: 'admin' | 'rep' | 'manager';
  status: 'active' | 'invited' | 'disabled';
}

export interface UseOrganizationUsersOptions {
  /**
   * Inclui também usuários desativados (`status='disabled'`). Use apenas em
   * contextos de FILTRO/leitura — usuários desativados continuam donos de
   * leads/negócios e precisam aparecer como opção de filtro. NÃO use em
   * formulários de atribuição (criar/editar/transferir), onde só faz sentido
   * escolher usuários ativos.
   */
  includeDisabled?: boolean;
}

export function useOrganizationUsers(options: UseOrganizationUsersOptions = {}) {
  const { includeDisabled = false } = options;
  const { orgId } = useCurrentOrgId();

  return useQuery({
    ...queryConfigs.structural,
    queryKey: ["organization-users", orgId, includeDisabled],
    queryFn: async () => {
      if (!orgId) throw new Error("Organization ID not found");

      // Buscar memberships. Por padrão só ativos; com includeDisabled trazemos
      // também os desativados (eles seguem como proprietários de registros).
      const statuses = includeDisabled ? ['active', 'disabled'] : ['active'];
      const { data: memberships, error: fetchError } = await supabase
        .from('memberships')
        .select(`
          user_id,
          role,
          status,
          created_at
        `)
        .eq('org_id', orgId)
        .in('status', statuses)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      if (!memberships || memberships.length === 0) {
        return [];
      }

      // Buscar dados dos perfis dos usuários ativos
      const userIds = memberships.map(m => m.user_id);
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, email, full_name, avatar_url')
        .in('user_id', userIds);

      if (profilesError) throw profilesError;

      // Combinar memberships com perfis
      const users = memberships.map(membership => {
        const profile = profiles?.find(p => p.user_id === membership.user_id);
        return {
          user_id: membership.user_id,
          email: profile?.email || '',
          full_name: profile?.full_name,
          avatar_url: profile?.avatar_url,
          role: membership.role,
          status: membership.status,
        };
      }).filter(user => user.full_name); // Apenas usuários com nome completo

      return users as unknown as OrganizationUser[];
    },
    enabled: !!orgId,
  });
}